/**
 * Messaging API Contracts
 *
 * Canonical wire schemas for the v1 messaging surface — broadcasts CRUD,
 * send, and shared primitives. Mirrors backend Zod schemas at
 * `apps/backend/src/api/messaging/v1/use-cases/broadcasts/**`. Update
 * this file and the backend in lockstep.
 *
 * Closes BE-MSG-T1-API-001 (shared-types/messaging missing) and
 * BE-MSG-T1-API-002 (web `CreateBroadcastPayload` drift —
 * `contentRecordId` removed; content is sent inline).
 */

import { z } from 'zod'

import { EmailDesignSchema } from '../../schemas/entities/email-design'

// Upper bound on a broadcast/email content body. EMAIL bodies are baked
// email-safe HTML and can be sizeable, but an unbounded string is a
// memory/abuse vector (persisted and shipped to OneSignal verbatim). Mirrors
// the backend Zod caps — keep in lockstep.
export const MAX_BROADCAST_BODY_LENGTH = 1_000_000

// Email template store contracts (school-scoped reusable designs, U5).
export * from './email-templates'

// ---------------------------------------------------------------------------
// Primitives
// ---------------------------------------------------------------------------

export const BroadcastChannelSchema = z.enum(['SMS', 'PUSH', 'EMAIL'])
export type BroadcastChannel = z.infer<typeof BroadcastChannelSchema>

export const BroadcastKindSchema = z.enum(['announcement', 'alert', 'event'])
export type BroadcastKind = z.infer<typeof BroadcastKindSchema>

export const BroadcastStatusSchema = z.enum(['draft', 'scheduled', 'sent', 'failed'])
export type BroadcastStatus = z.infer<typeof BroadcastStatusSchema>

/**
 * Attachment shape on the broadcast content payload. Lowercase casing
 * matches the backend Zod schemas (`broadcasts/create/schema.ts`).
 */
export const BroadcastContentAttachmentSchema = z.object({
  type: z.enum(['image', 'file', 'video']),
  url: z.string().url(),
  isCover: z.boolean().optional(),
  name: z.string().optional()
})
export type BroadcastContentAttachment = z.infer<typeof BroadcastContentAttachmentSchema>

/**
 * Inline content body for create/update broadcast.
 */
export const BroadcastContentInputSchema = z.object({
  // Capped to match the backend create/update Zod schemas (lockstep).
  title: z.string().max(2000).optional(),
  body: z.string().min(1).max(MAX_BROADCAST_BODY_LENGTH),
  attachments: z.array(BroadcastContentAttachmentSchema).optional()
})
export type BroadcastContentInput = z.infer<typeof BroadcastContentInputSchema>

// ---------------------------------------------------------------------------
// Response sub-shapes
// ---------------------------------------------------------------------------

export const BroadcastContentInfoSchema = z.object({
  id: z.string(),
  title: z.string().nullable(),
  body: z.string().nullable()
})
export type BroadcastContentInfo = z.infer<typeof BroadcastContentInfoSchema>

export const BroadcastSchoolInfoSchema = z.object({
  id: z.string(),
  name: z.string()
})
export type BroadcastSchoolInfo = z.infer<typeof BroadcastSchoolInfoSchema>

export const BroadcastCreatorInfoSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  profileImageUrl: z.string().nullable()
})
export type BroadcastCreatorInfo = z.infer<typeof BroadcastCreatorInfoSchema>

/**
 * Broadcast response shape. Note: timestamps are ISO-8601 strings on the
 * wire (JSON.stringify(Date) produces strings). The backend service-layer
 * `BroadcastResponse` interface types these as `Date`; that interface
 * describes the in-process value before JSON serialization — the wire
 * format is always string.
 */
export const BroadcastResponseSchema = z.object({
  id: z.string(),
  kind: BroadcastKindSchema,
  status: BroadcastStatusSchema,
  channels: z.array(BroadcastChannelSchema),
  content: BroadcastContentInfoSchema,
  school: BroadcastSchoolInfoSchema,
  createdBy: BroadcastCreatorInfoSchema.optional(),
  recipientGroupIds: z.array(z.string()).nullable(),
  recipientUserIds: z.array(z.string()).nullable(),
  recipientFilters: z.record(z.string(), z.unknown()).nullable(),
  recipientCount: z.number().optional(),
  deliveredCount: z.number().optional(),
  openedCount: z.number().optional(),
  scheduledAt: z.string().nullable(),
  sentAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string().nullable(),
  deletedAt: z.string().nullable(),
  metadata: z.record(z.string(), z.unknown()).optional()
})
export type BroadcastResponse = z.infer<typeof BroadcastResponseSchema>

export const BroadcastRecipientDetailSchema = z.object({
  id: z.string(),
  channel: BroadcastChannelSchema,
  contactInfo: z.string().nullable(),
  user: z.object({
    id: z.string(),
    firstName: z.string().nullable(),
    lastName: z.string().nullable()
  }),
  sentAt: z.string().nullable(),
  deliveredAt: z.string().nullable(),
  openedAt: z.string().nullable(),
  convertedAt: z.string().nullable(),
  deliveryError: z.string().nullable()
})
export type BroadcastRecipientDetail = z.infer<typeof BroadcastRecipientDetailSchema>

// ---------------------------------------------------------------------------
// POST /v1/messaging/broadcasts
// ---------------------------------------------------------------------------

/**
 * Mirrors `apps/backend/src/api/messaging/v1/use-cases/broadcasts/create/schema.ts#CreateBroadcastRequest`.
 *
 * NOTE: there is NO `contentRecordId` field. Broadcast content is created
 * inline alongside the broadcast — see BE-MSG-T1-API-002.
 */
export const CreateBroadcastRequestSchema = z.object({
  idempotencyKey: z.string().optional(),
  schoolId: z.string(),
  kind: BroadcastKindSchema,
  channels: z.array(BroadcastChannelSchema).min(1),
  content: BroadcastContentInputSchema,
  recipientGroupIds: z.array(z.string()).optional(),
  recipientUserIds: z.array(z.string()).optional(),
  recipientFilters: z.record(z.string(), z.unknown()).optional(),
  /**
   * @deprecated The legacy `/v1/messaging/broadcasts` endpoints reject
   * `scheduledAt` with a 400 ("Scheduled sending is not supported; send now")
   * because nothing dispatches scheduled legacy broadcasts (R107). Omit it and
   * send now.
   */
  scheduledAt: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  // Atomic create-and-send. When `true`, the broadcast is dispatched in
  // the same request and the just-created draft is rolled back on send
  // failure — replaces the legacy two-call create-then-send flow.
  sendImmediately: z.boolean().optional()
})
export type CreateBroadcastRequest = z.infer<typeof CreateBroadcastRequestSchema>

export const CreateBroadcastResponseSchema = z.object({
  data: BroadcastResponseSchema,
  message: z.string().optional()
})
export type CreateBroadcastResponse = z.infer<typeof CreateBroadcastResponseSchema>

// ---------------------------------------------------------------------------
// PATCH /v1/messaging/broadcasts/:id
// ---------------------------------------------------------------------------

/**
 * Mirrors backend `UpdateBroadcastRequest`. `id` is the path param;
 * the rest are body fields, all optional.
 */
export const UpdateBroadcastRequestSchema = z.object({
  channels: z.array(BroadcastChannelSchema).min(1).optional(),
  recipientGroupIds: z.array(z.string()).optional(),
  recipientUserIds: z.array(z.string()).optional(),
  recipientFilters: z.record(z.string(), z.unknown()).optional(),
  /**
   * @deprecated The legacy `/v1/messaging/broadcasts` endpoints reject
   * `scheduledAt` with a 400 ("Scheduled sending is not supported; send now")
   * because nothing dispatches scheduled legacy broadcasts (R107). Omit it and
   * send now.
   */
  scheduledAt: z.string().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  content: z
    .object({
      title: z.string().max(2000).optional(),
      body: z.string().min(1).max(MAX_BROADCAST_BODY_LENGTH).optional(),
      attachments: z.array(BroadcastContentAttachmentSchema).optional()
    })
    .optional()
})
export type UpdateBroadcastRequest = z.infer<typeof UpdateBroadcastRequestSchema>

export const UpdateBroadcastResponseSchema = z.object({
  data: BroadcastResponseSchema,
  message: z.string().optional()
})
export type UpdateBroadcastResponse = z.infer<typeof UpdateBroadcastResponseSchema>

// ---------------------------------------------------------------------------
// GET /v1/messaging/broadcasts (list)
// ---------------------------------------------------------------------------

export const ListBroadcastsRequestSchema = z.object({
  limit: z.number().positive().max(100).optional(),
  offset: z.number().min(0).optional(),
  schoolId: z.string().optional(),
  status: BroadcastStatusSchema.optional(),
  kind: BroadcastKindSchema.optional(),
  createdAfter: z.string().optional(),
  createdBefore: z.string().optional(),
  sentAfter: z.string().optional(),
  sentBefore: z.string().optional(),
  searchQuery: z.string().optional(),
  orderBy: z.enum(['createdAt', 'sentAt', 'scheduledAt']).optional(),
  orderDirection: z.enum(['asc', 'desc']).optional(),
  includeDeleted: z.boolean().optional()
})
export type ListBroadcastsRequest = z.infer<typeof ListBroadcastsRequestSchema>

export const ListBroadcastsResponseSchema = z.object({
  message: z.string().optional(),
  data: z.array(BroadcastResponseSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  })
})
export type ListBroadcastsResponse = z.infer<typeof ListBroadcastsResponseSchema>

// ---------------------------------------------------------------------------
// GET /v1/messaging/broadcasts/:id
// ---------------------------------------------------------------------------

export const GetBroadcastRequestSchema = z.object({
  includeRecipients: z.boolean().optional()
})
export type GetBroadcastRequest = z.infer<typeof GetBroadcastRequestSchema>

export const GetBroadcastResponseSchema = z.object({
  message: z.string().optional(),
  data: BroadcastResponseSchema.extend({
    recipients: z.array(BroadcastRecipientDetailSchema).optional()
  })
})
export type GetBroadcastResponse = z.infer<typeof GetBroadcastResponseSchema>

// ---------------------------------------------------------------------------
// POST /v1/messaging/broadcasts/:id/send
// ---------------------------------------------------------------------------

export const SendBroadcastRequestSchema = z.object({
  sendNow: z.boolean().optional()
})
export type SendBroadcastRequest = z.infer<typeof SendBroadcastRequestSchema>

// ---------------------------------------------------------------------------
// Email broadcast metadata
// ---------------------------------------------------------------------------

/**
 * Typed view of an EMAIL broadcast's `metadata` blob. The broadcast
 * create/update request still carries `metadata` as an open record on the
 * wire (backward-compatible); this schema is the canonical shape the email
 * builder writes and the bake/send paths read:
 *
 *   - `design`   — the Puck `Data` produced by the email builder, validated
 *                  against `EmailDesignSchema` (the silent-block-drop guard).
 *   - `fromName` — the configured sender display name, applied on send (U8).
 *
 * `.passthrough()` keeps it forward-compatible with metadata keys other
 * channels (SMS/PUSH) may carry.
 */
export const EmailBroadcastMetadataSchema = z
  .object({
    design: EmailDesignSchema.optional(),
    fromName: z.string().optional()
  })
  .passthrough()
export type EmailBroadcastMetadata = z.infer<typeof EmailBroadcastMetadataSchema>

// ---------------------------------------------------------------------------
// POST /v1/messaging/broadcasts/test-send
// ---------------------------------------------------------------------------

/**
 * Test-send request — the ALREADY-BAKED email content only.
 *
 * There is deliberately NO recipient field: the backend sends the test to the
 * caller's OWN session email, so the endpoint can never be an open relay for
 * school-branded mail. `{{school_name}}` is substituted at bake time on the
 * web side, so `html` arrives fully substituted. Mirrors the backend Zod schema
 * at `apps/backend/src/api/messaging/v1/use-cases/broadcasts/test-send/schema.ts`.
 */
export const TestSendEmailRequestSchema = z.object({
  subject: z.string().min(1).max(998),
  html: z.string().min(1).max(MAX_BROADCAST_BODY_LENGTH),
  fromName: z.string().max(255).optional()
})
export type TestSendEmailRequest = z.infer<typeof TestSendEmailRequestSchema>

export const TestSendEmailResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    notificationId: z.string().nullable(),
    sentTo: z.string()
  })
})
export type TestSendEmailResponse = z.infer<typeof TestSendEmailResponseSchema>
